import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { showError } from '@utils';
import {
  boardsServices,
  sellVehiclesServices,
  fleetVehiclesServices,
} from '@services';
import { useQuery } from 'react-query';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees } from 'hooks/common';
import { normalizeSellRequestsTaskData, normalizeVehicleData } from './utils';
import { SELL_REQUESTS_BOARD } from './constants';

const stages = SELL_REQUESTS_BOARD.statusOrder.reduce((acc, cur) => {
  acc.push({
    text: SELL_REQUESTS_BOARD.statuses[cur].label,
    value: Number(cur),
  });

  return acc;
}, []);

export const useSellRequestsTaskOperations = () => {
  const {
    query: { sellRequestsTaskId },
  } = useRouter();
  const { assignees, isFetching: isAssigneesFetching } = useAssignees();

  const sellRequestsTaskResponse = useQuery(
    [QUERY_KEYS.GET_SELL_REQUESTS_TASK_KEY, sellRequestsTaskId],
    () => boardsServices.getTaskById(sellRequestsTaskId),
    {
      enabled: !!sellRequestsTaskId,
    }
  );

  const task = useMemo(
    () =>
      normalizeSellRequestsTaskData(
        sellRequestsTaskResponse?.data?.data,
        assignees,
        stages
      ),
    [sellRequestsTaskResponse?.data?.data, assignees]
  );

  const saleId = sellRequestsTaskResponse?.data?.data?.customFields?.saleId;
  const carId = sellRequestsTaskResponse?.data?.data?.customFields?.carId;

  const vehicleResponse = useQuery(
    ['sellVehicle', saleId],
    () => sellVehiclesServices.getSellVehicle(saleId),
    {
      enabled: !!saleId,
    }
  );

  const instacarVehicleResponse = useQuery(
    ['instacarVehicle', carId],
    () => fleetVehiclesServices.getVehicleByInternalId(carId),
    {
      enabled: !!carId,
    }
  );

  const vehicle = normalizeVehicleData({
    ...vehicleResponse?.data?.data,
    procurement: instacarVehicleResponse?.data?.data?.procurement,
    id: instacarVehicleResponse?.data?.data?.id,
  });

  const mentionedUsers = assignees
    ?.filter((user) => user?.isActive === true)
    ?.map?.((user) => {
      return { id: user?.email, display: user?.text };
    });

  const updateTask = async (payload = {}) => {
    try {
      if (!payload?.id) return;

      const taskId = payload?.id;
      await boardsServices.updateTask(taskId, payload);
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    if (sellRequestsTaskResponse?.isError) {
      showError(sellRequestsTaskResponse?.error);
    }
  }, [sellRequestsTaskResponse?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (vehicleResponse?.isError) {
      showError(vehicleResponse?.error);
    }
  }, [vehicleResponse?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (instacarVehicleResponse?.isError) {
      showError(instacarVehicleResponse?.error);
    }
  }, [instacarVehicleResponse?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading =
    sellRequestsTaskResponse?.isFetching ||
    vehicleResponse?.isFetching ||
    instacarVehicleResponse?.isFetching ||
    isAssigneesFetching;

  const refetch = () => {
    sellRequestsTaskResponse.refetch();
    vehicleResponse.refetch();
    instacarVehicleResponse.refetch();
  };

  return {
    loading,
    vehicle,
    task,
    stages,
    taskId: sellRequestsTaskId,
    mentionedUsers,
    assignees,
    getSellRequestsTask: refetch,
    updateTask,
  };
};
